import { Box, useBreakpointValue } from '@chakra-ui/react';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

const SplitViewContext = createContext(false);
export const useIsSplitView = () => useContext(SplitViewContext);

interface SplitViewContainerProps {
  leftContent: React.ReactNode;
  rightContent: React.ReactNode;
  onClose?: () => void;
}

const STORAGE_KEY = 'splitViewRatio';
const DEFAULT_RATIO = 0.5;
const MIN_LEFT_WIDTH = 400;
const MIN_RIGHT_WIDTH = 300;
const HARD_MIN_RATIO = 0.2;
const HARD_MAX_RATIO = 0.8;
/** Keyboard resize step, in px — converted to a ratio against the live width. */
const KEY_STEP_PX = 16;
const KEY_STEP_PX_COARSE = 64;

/**
 * Both pane minimums, then the outer bounds. Order matters: when a container is
 * too narrow to honour both minimums the left one wins, which is what the
 * original nested Math.max/min did.
 */
function clampRatio(candidate: number, containerWidth: number): number {
  const minRatio = Math.max(HARD_MIN_RATIO, MIN_LEFT_WIDTH / containerWidth);
  const maxRatio = Math.min(HARD_MAX_RATIO, 1 - MIN_RIGHT_WIDTH / containerWidth);
  const clamped = Math.max(minRatio, Math.min(candidate, maxRatio));
  // Round to sub-pixel precision so repeated keyboard steps don't accumulate
  // float noise into localStorage (0.5640000000000001).
  return Math.round(clamped * 1e4) / 1e4;
}

/**
 * SplitViewContainer - Renders two panes side-by-side with a draggable divider
 *
 * Used for comparing OCR-created notes with their source attachments.
 * Only active on large screens (lg breakpoint+); Layout.tsx handles fallback to overlay.
 *
 * Layout:
 * ┌─────────────────────┬───┬─────────────────────┐
 * │                     │ │ │                     │
 * │    Left Content     │ │ │    Right Content    │
 * │    (Note Editor)    │ │ │    (Attachment)     │
 * │                     │ │ │                     │
 * └─────────────────────┴───┴─────────────────────┘
 *                        ↑
 *                   Draggable divider
 */
const SplitViewContainer: React.FC<SplitViewContainerProps> = ({
  leftContent,
  rightContent,
  onClose: _onClose,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [ratio, setRatio] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = parseFloat(saved);
      if (!isNaN(parsed) && parsed >= 0.2 && parsed <= 0.8) {
        return parsed;
      }
    }
    return DEFAULT_RATIO;
  });

  // Only show on md breakpoint and up
  const showDivider = useBreakpointValue({ base: false, md: true }) || false;

  // Save ratio to localStorage when it changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, ratio.toString());
  }, [ratio]);

  // Drag origin. A ref, not state, so the move handler stays stable for the
  // whole drag instead of being rebuilt (and re-bound) on every frame.
  const dragOriginRef = useRef<{ pointerX: number; ratio: number } | null>(null);

  /** Live container width, or null when it can't be measured (unmounted / 0). */
  const measureContainer = useCallback(() => {
    const width = containerRef.current?.getBoundingClientRect().width;
    return width && width > 0 ? width : null;
  }, []);

  const handleResizeStart = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return;
      e.preventDefault();
      dragOriginRef.current = { pointerX: e.clientX, ratio };
      // Pointer capture keeps move/up flowing to the divider when the pointer
      // leaves the WINDOW — the document-level mouseup the old version relied
      // on never fires there, so the divider stayed glued to the cursor until
      // the next click. (The PDF pane can't steal the stream either way: both
      // panes drop pointer-events while isDragging.)
      e.currentTarget.setPointerCapture?.(e.pointerId);
      setIsDragging(true);
    },
    [ratio],
  );

  const handleResizeMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const origin = dragOriginRef.current;
      const containerWidth = measureContainer();
      if (!origin || !containerWidth) return;
      const delta = (e.clientX - origin.pointerX) / containerWidth;
      setRatio(clampRatio(origin.ratio + delta, containerWidth));
    },
    [measureContainer],
  );

  const handleResizeEnd = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragOriginRef.current) return;
    dragOriginRef.current = null;
    e.currentTarget.releasePointerCapture?.(e.pointerId);
    setIsDragging(false);
  }, []);

  // Keyboard resize — the divider is focusable, so it has to be operable
  // without a pointer.
  const handleResizeKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      const containerWidth = measureContainer();
      if (!containerWidth) return;
      e.preventDefault();
      const stepPx = e.shiftKey ? KEY_STEP_PX_COARSE : KEY_STEP_PX;
      const delta = (e.key === 'ArrowLeft' ? -stepPx : stepPx) / containerWidth;
      setRatio((prev) => clampRatio(prev + delta, containerWidth));
    },
    [measureContainer],
  );

  useEffect(() => {
    if (!isDragging) return;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    return () => {
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging]);

  const leftWidthPercent = ratio * 100;
  const rightWidthPercent = (1 - ratio) * 100;

  return (
    <Box
      ref={containerRef}
      data-print="scroller"
      display="flex"
      flexDirection="row"
      // `100%` of the panel, NOT the viewport. Since the 2026-09 curved canvas
      // the parent is inset 12px top and bottom, so it is `calc(100vh - 24px)`
      // tall with `overflow: hidden` — re-asserting `100vh` here makes this
      // 24px taller than the box clipping it, and because both panes scroll
      // INSIDE this element the overflow is unreachable: the bottom 24px of a
      // note is simply gone at full scroll, with no scrollbar to recover it.
      h="100%"
      w="100%"
      overflow="hidden"
      position="relative"
    >
      {/* Left Pane - Note Content */}
      <Box
        data-print="scroller"
        w={`${leftWidthPercent}%`}
        minW={`${MIN_LEFT_WIDTH}px`}
        h="100%"
        overflow="auto"
        // `frame.panel`, the same fill as the right pane and as the panel this
        // container is inset into (`Layout.tsx`). In normal mode the note is a
        // direct child of that panel and renders on white; leaving this pane on
        // `bg.canvas` drew the SAME note parchment as soon as a split opened,
        // and the parchment one is the primary pane. Named rather than left
        // `transparent` so both panes spell the intent the same way and the
        // pairing guard can read it off the render.
        bg="frame.panel"
        flexShrink={0}
        pointerEvents={isDragging ? 'none' : undefined}
      >
        <SplitViewContext.Provider value={true}>{leftContent}</SplitViewContext.Provider>
      </Box>

      {/* Draggable Divider. Stays a 4px gutter, but the ::after box extends the
          hit area 4px into each pane — a bare 4px target is a coin flip to grab
          with a trackpad. The pseudo-element takes part in hit-testing and its
          events target this Box, so no extra element is needed.
          `zIndex` is load-bearing, and has to clear the RIGHT pane's own
          chrome: at `auto` the ::after paints above the left pane (earlier in
          DOM order) but below any positioned descendant of the right one, so
          the right-hand 4px went dead wherever the pane has positioned chrome.
          Measured in-app against the attachment view: `DetailTopBar` is
          `position: sticky; z-index: 10`, so across its 48px the band was
          -4…+2 at BOTH `auto` and `zIndex: 1` — 11 is the first value that
          restores the full -4…+7 at every height. */}
      {showDivider && (
        <Box
          w="4px"
          minW="4px"
          maxW="4px"
          h="100%"
          position="relative"
          zIndex={11}
          cursor="col-resize"
          sx={{ touchAction: 'none' }}
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize split view"
          // A focusable separator is a window splitter; without these, screen
          // readers announce a made-up "50". Reported as the left pane's
          // percentage — the runtime clamp is tighter than 20/80 whenever the
          // container is narrow enough for the pane minimums to bite.
          aria-valuenow={Math.round(ratio * 100)}
          aria-valuemin={Math.round(HARD_MIN_RATIO * 100)}
          aria-valuemax={Math.round(HARD_MAX_RATIO * 100)}
          tabIndex={0}
          onPointerDown={handleResizeStart}
          onPointerMove={handleResizeMove}
          onPointerUp={handleResizeEnd}
          onPointerCancel={handleResizeEnd}
          onKeyDown={handleResizeKeyDown}
          bg={isDragging ? 'brand.primary' : 'border.muted'}
          _hover={{ bg: 'brand.primary' }}
          _focusVisible={{ outline: 'none', bg: 'brand.primary' }}
          transition="background-color 0.15s ease"
          flexShrink={0}
          _after={{
            content: '""',
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: '-4px',
            right: '-4px',
          }}
        />
      )}

      {/* Right Pane - Attachment Content */}
      <Box
        data-print="scroller"
        w={`${rightWidthPercent}%`}
        minW={`${MIN_RIGHT_WIDTH}px`}
        h="100%"
        overflow="hidden"
        // `frame.panel`, not `bg.surface`. The 2026-09 flip repointed
        // `bg.surface` from white to parchment, which would have made this pane
        // a parchment box inside the white panel. Both panes are the panel
        // itself — the divider is what separates them, and each pane's own
        // content draws its boxes on top.
        bg="frame.panel"
        borderLeft={showDivider ? 'none' : '1px'}
        borderColor="border.muted"
        display="flex"
        flexDirection="column"
        flexShrink={0}
        pointerEvents={isDragging ? 'none' : undefined}
      >
        <SplitViewContext.Provider value={true}>{rightContent}</SplitViewContext.Provider>
      </Box>
    </Box>
  );
};

export default SplitViewContainer;
